<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="() => {}"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="settings-view">
      <div class="page-header">
        <h1 class="page-title">{{ $t('settings.title') }}</h1>
        <p class="page-subtitle">{{ $t('settings.subtitle') }}</p>
        <span class="account-id">Account ID: {{ businessId }}</span>
      </div>

      <div class="settings-sections">
        <!-- Module Settings -->
        <IntercomCard class="settings-card">
          <h3>{{ $t('settings.moduleSettings') }}</h3>
          <div class="form-grid">
            <div class="form-field">
              <label>{{ $t('settings.defaultSchool') }}</label>
              <select
                v-model="settings.default_school"
                class="select-input"
                @change="saveSettings"
              >
                <option value="hanafi">{{ $t('calculator.hanafi') }}</option>
                <option value="shia_ithna_ashari">{{ $t('calculator.shiaIthnaAshari') }}</option>
              </select>
            </div>
            <div class="form-field">
              <label>{{ $t('settings.reviewSLAHours') }}</label>
              <input
                type="number"
                v-model.number="settings.review_sla_hours"
                min="1"
                class="select-input"
                @blur="saveSettings"
              />
            </div>
            <div class="form-field">
              <label>{{ $t('settings.autoCloseDays') }}</label>
              <input
                type="number"
                v-model.number="settings.auto_close_days"
                min="1"
                class="select-input"
                @blur="saveSettings"
              />
            </div>
            <div class="form-field">
              <label>{{ $t('settings.infoReminderDays') }}</label>
              <input
                type="number"
                v-model.number="settings.info_reminder_days"
                min="1"
                class="select-input"
                @blur="saveSettings"
              />
            </div>
            <div class="form-field">
              <label>{{ $t('settings.maxCasesPerScholar') }}</label>
              <input
                type="number"
                v-model.number="settings.max_cases_per_scholar"
                min="1"
                class="select-input"
                @blur="saveSettings"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- WhatsApp Notifications -->
        <IntercomCard class="settings-card">
          <h3>{{ $t('settings.whatsapp') }}</h3>
          <div class="toggle-list">
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.autoNotify') }}</span>
                <span class="toggle-desc">{{ $t('settings.autoNotifyDesc') }}</span>
              </div>
              <IntercomToggle
                :model-value="settings.whatsapp_auto_notify"
                @update:model-value="(val) => { settings.whatsapp_auto_notify = val; saveSettings() }"
              />
            </div>
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.scholarNotification') }}</span>
                <span class="toggle-desc">{{ $t('settings.scholarNotificationDesc') }}</span>
              </div>
              <IntercomToggle
                :model-value="settings.scholar_notification"
                @update:model-value="(val) => { settings.scholar_notification = val; saveSettings() }"
              />
            </div>
          </div>
        </IntercomCard>

        <!-- Certificate Settings -->
        <IntercomCard class="settings-card">
          <h3>{{ $t('settings.certificate') }}</h3>
          <div class="toggle-list">
            <div class="toggle-row">
              <div class="toggle-info">
                <span class="toggle-label">{{ $t('settings.includeQuranRefs') }}</span>
                <span class="toggle-desc">{{ $t('settings.includeQuranRefsDesc') }}</span>
              </div>
              <IntercomToggle
                :model-value="settings.pdf_include_quran_refs"
                @update:model-value="(val) => { settings.pdf_include_quran_refs = val; saveSettings() }"
              />
            </div>
          </div>
          <div class="form-field" style="margin-top: var(--space-4)">
            <label>{{ $t('settings.disclaimerText') }}</label>
            <textarea
              v-model="settings.disclaimer_text"
              :placeholder="$t('settings.disclaimerPlaceholder')"
              class="textarea-input"
              rows="4"
              @blur="saveSettings"
            ></textarea>
          </div>
        </IntercomCard>
      </div>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomCard,
  IntercomToggle,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, businessId, fetchBusiness } = useBusiness()
const { getSettings, updateSettings } = useFaraid()
const { navSections } = useNavigation()

const settings = reactive({
  default_school: 'hanafi',
  review_sla_hours: 48,
  auto_close_days: 30,
  info_reminder_days: 7,
  max_cases_per_scholar: 10,
  whatsapp_auto_notify: true,
  scholar_notification: true,
  pdf_include_quran_refs: true,
  disclaimer_text: '',
})

const fetchSettings = async () => {
  try {
    const response = await getSettings()
    const data = response.settings || response
    Object.assign(settings, {
      default_school: data.default_school || 'hanafi',
      review_sla_hours: data.review_sla_hours || 48,
      auto_close_days: data.auto_close_days || 30,
      info_reminder_days: data.info_reminder_days || 7,
      max_cases_per_scholar: data.max_cases_per_scholar || 10,
      whatsapp_auto_notify: data.whatsapp_auto_notify ?? true,
      scholar_notification: data.scholar_notification ?? true,
      pdf_include_quran_refs: data.pdf_include_quran_refs ?? true,
      disclaimer_text: data.disclaimer_text || '',
    })
  } catch (error) {
    console.error('Failed to fetch settings:', error)
  }
}

let saveTimeout = null
const saveSettings = () => {
  // Debounce auto-save
  clearTimeout(saveTimeout)
  saveTimeout = setTimeout(async () => {
    try {
      await updateSettings({
        default_school: settings.default_school,
        review_sla_hours: settings.review_sla_hours,
        auto_close_days: settings.auto_close_days,
        info_reminder_days: settings.info_reminder_days,
        max_cases_per_scholar: settings.max_cases_per_scholar,
        whatsapp_auto_notify: settings.whatsapp_auto_notify,
        scholar_notification: settings.scholar_notification,
        pdf_include_quran_refs: settings.pdf_include_quran_refs,
        disclaimer_text: settings.disclaimer_text,
      })
      toast.success(t('success.settingsUpdated'))
    } catch (error) {
      toast.error(error.message || t('error.saveFailed'))
    }
  }, 500)
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchSettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.settings-view {
  padding: var(--space-8);
  max-width: 800px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.account-id {
  display: inline-block;
  font-size: 12px;
  color: var(--text-tertiary, #98a2b3);
  background: var(--bg-secondary, #f9fafb);
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  margin-top: 4px;
}

.settings-sections {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.settings-card {
  padding: var(--space-6);
}

.settings-card h3 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-5) 0;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.form-field label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
}

.select-input {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  background: var(--color-white);
}

.select-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.textarea-input {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.textarea-input:focus {
  outline: none;
  border-color: var(--color-blue);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

/* Toggle List */
.toggle-list {
  display: flex;
  flex-direction: column;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.toggle-row:last-child {
  border-bottom: none;
}

.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.toggle-desc {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

@media (max-width: 768px) {
  .settings-view {
    padding: var(--space-4);
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
